import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CostCategory, CostItem, WastageCategory, WastageItem } from "../types";
import { costService, wastageService } from "../services/dataService";
import { formatCurrency } from "../utils/formatters";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import "./CostManagement.css";

interface CostManagementProps {}

const CostManagement: React.FC<CostManagementProps> = () => {
  const [activeTab, setActiveTab] = useState<
    "categories" | "items" | "wastage"
  >("categories");
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<CostCategory[]>([]);
  const [costItems, setCostItems] = useState<CostItem[]>([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] =
    useState<string>("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CostItem | null>(null);
  const [editCategoryModalOpen, setEditCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CostCategory | null>(
    null,
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );

  // Fire yönetimi state'leri
  const [wastageCategories, setWastageCategories] = useState<WastageCategory[]>(
    [],
  );
  const [wastageItems, setWastageItems] = useState<WastageItem[]>([]);
  const [selectedWastageCategoryFilter, setSelectedWastageCategoryFilter] =
    useState<string>("");
  const [editingWastageCategory, setEditingWastageCategory] =
    useState<WastageCategory | null>(null);
  const [editingWastageItem, setEditingWastageItem] =
    useState<WastageItem | null>(null);
  const [wastageModalOpen, setWastageModalOpen] = useState(false);
  const [wastageItemModalOpen, setWastageItemModalOpen] = useState(false);

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  const navigate = useNavigate();

  useEffect(() => {
    if (activeTab === "categories") {
      loadCategories();
      loadCostItems(); // Kategoriler için de maliyet kalemlerini yükle
    } else if (activeTab === "items") {
      loadCostItems();
      loadCategories(); // Kategori filtresi için kategorileri de yükle
    } else if (activeTab === "wastage") {
      loadWastageCategories();
      loadWastageItems();
    }
  }, [activeTab]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await costService.getCostCategories();
      setCategories(data);
    } catch (error) {
      console.error("❌ Failed to load categories:", error);
      setAlertModal({
        isOpen: true,
        message: "Kategoriler yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCostItems = async () => {
    try {
      setLoading(true);
      const data = await costService.getCostItems();
      setCostItems(data);
    } catch (error) {
      console.error("❌ Failed to load cost items:", error);
      setAlertModal({
        isOpen: true,
        message: "Maliyet kalemleri yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadWastageCategories = async () => {
    try {
      setLoading(true);
      const data = await wastageService.getCategories();
      setWastageCategories(data);
    } catch (error) {
      console.error("❌ Failed to load wastage categories:", error);
      setAlertModal({
        isOpen: true,
        message: "Fire kategorileri yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadWastageItems = async () => {
    try {
      setLoading(true);
      const data = await wastageService.getItems();
      setWastageItems(data);
    } catch (error) {
      console.error("❌ Failed to load wastage items:", error);
      setAlertModal({
        isOpen: true,
        message: "Fire kalemleri yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("tr-TR");
  };

  // Kategori adını bul
  const getCategoryName = (categoryId: string): string => {
    const category = categories.find((c) => c.costCategoryId === categoryId);
    return category?.categoryName || "-";
  };

  const handleEditItem = (item: CostItem) => {
    setEditingItem(item);
    setEditModalOpen(true);
  };

  const handleSaveEdit = async (updatedItem: CostItem) => {
    if (!editingItem) return;

    try {
      setLoading(true);
      await costService.updateCostItem(editingItem.costItemId, updatedItem);
      setAlertModal({
        isOpen: true,
        message: "Maliyet kalemi başarıyla güncellendi!",
        type: "success",
      });
      setEditModalOpen(false);
      setEditingItem(null);
      loadCostItems();
    } catch (error) {
      console.error("Failed to update cost item:", error);
      setAlertModal({
        isOpen: true,
        message: "Maliyet kalemi güncellenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (category: CostCategory) => {
    setEditingCategory(category);
    setEditCategoryModalOpen(true);
  };

  const handleSaveCategoryEdit = async (updatedCategory: CostCategory) => {
    if (!editingCategory) return;

    try {
      setLoading(true);
      await costService.updateCostCategory(
        editingCategory.costCategoryId,
        updatedCategory,
      );
      setAlertModal({
        isOpen: true,
        message: "Kategori başarıyla güncellendi!",
        type: "success",
      });
      setEditCategoryModalOpen(false);
      setEditingCategory(null);
      loadCategories();
    } catch (error) {
      console.error("Failed to update category:", error);
      setAlertModal({
        isOpen: true,
        message: "Kategori güncellenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fire kategori CRUD fonksiyonları
  const handleSaveWastageCategory = async (data: WastageCategory) => {
    try {
      setLoading(true);
      if (data.wastageCategoryId) {
        // Güncelleme
        await wastageService.updateCategory(data.wastageCategoryId, {
          categoryName: data.categoryName,
          description: data.description,
          color: data.color,
          icon: data.icon,
          displayOrder: data.displayOrder,
        });
        setAlertModal({
          isOpen: true,
          message: "Fire kategorisi güncellendi!",
          type: "success",
        });
      } else {
        // Yeni ekleme
        await wastageService.createCategory({
          categoryName: data.categoryName,
          description: data.description,
          color: data.color,
          icon: data.icon,
          displayOrder: data.displayOrder,
        });
        setAlertModal({
          isOpen: true,
          message: "Fire kategorisi eklendi!",
          type: "success",
        });
      }
      setWastageModalOpen(false);
      setEditingWastageCategory(null);
      loadWastageCategories();
    } catch (error) {
      console.error("Failed to save wastage category:", error);
      setAlertModal({
        isOpen: true,
        message: "Fire kategorisi kaydedilemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fire kalemi CRUD fonksiyonları
  const handleSaveWastageItem = async (data: WastageItem) => {
    try {
      setLoading(true);
      if (data.wastageItemId) {
        // Güncelleme
        await wastageService.updateItem(data.wastageItemId, {
          wastageCategoryId: data.wastageCategoryId,
          itemName: data.itemName,
          description: data.description,
          defaultPercentage: data.defaultPercentage,
          unit: data.unit,
          processStep: data.processStep,
          reason: data.reason,
          isCalculated: data.isCalculated,
          notes: data.notes,
        });
        setAlertModal({
          isOpen: true,
          message: "Fire kalemi güncellendi!",
          type: "success",
        });
      } else {
        // Yeni ekleme
        await wastageService.createItem({
          wastageCategoryId: data.wastageCategoryId,
          itemName: data.itemName,
          description: data.description,
          defaultPercentage: data.defaultPercentage,
          unit: data.unit,
          processStep: data.processStep,
          reason: data.reason,
          isCalculated: data.isCalculated,
          notes: data.notes,
        });
        setAlertModal({
          isOpen: true,
          message: "Fire kalemi eklendi!",
          type: "success",
        });
      }
      setWastageItemModalOpen(false);
      setEditingWastageItem(null);
      loadWastageItems();
    } catch (error) {
      console.error("Failed to save wastage item:", error);
      setAlertModal({
        isOpen: true,
        message: "Fire kalemi kaydedilemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (
    categoryId: string,
    categoryName: string,
  ) => {
    setConfirmModal({
      isOpen: true,
      message: `"${categoryName}" kategorisini silmek istediğinize emin misiniz?\n\nBu kategoriye ait maliyet kalemleri de silinecektir!`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await costService.deleteCostCategory(categoryId);
          setAlertModal({
            isOpen: true,
            message: "Kategori başarıyla silindi!",
            type: "success",
          });
          loadCategories();
          loadCostItems();
        } catch (error) {
          console.error("Failed to delete category:", error);
          setAlertModal({
            isOpen: true,
            message: "Kategori silinemedi! Bu kategori kullanımda olabilir.",
            type: "error",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Maliyet kalemi silme helper fonksiyonu
  const handleDeleteCostItem = (itemId: string, itemName: string) => {
    setConfirmModal({
      isOpen: true,
      message: `"${itemName}" maliyet kalemini silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz!`,
      type: "danger",
      onConfirm: async () => {
        try {
          await costService.deleteCostItem(itemId);
          setAlertModal({
            isOpen: true,
            message: "Maliyet kalemi başarıyla silindi!",
            type: "success",
          });
          loadCostItems();
        } catch (error) {
          console.error("Failed to delete cost item:", error);
          setAlertModal({
            isOpen: true,
            message: "Maliyet kalemi silinemedi! Bu kalem kullanımda olabilir.",
            type: "error",
          });
        }
      },
    });
  };

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const getCategoryItems = (categoryId: string) => {
    return costItems.filter((item) => item.costCategoryId === categoryId);
  };

  if (loading) {
    return <PageLoader message="Maliyet verileri yükleniyor..." />;
  }

  return (
    <div className="cost-management-container">
      <div className="cost-management-header">
        <div className="header-content">
          <div className="header-top">
            <h1>Maliyet Yönetimi</h1>
            <p className="header-subtitle">
              Kategori, kalem ve birim tanımlamaları
            </p>
          </div>
        </div>
        <div className="tab-navigation">
          <button
            className={`tab-button ${
              activeTab === "categories" ? "active" : ""
            }`}
            onClick={() => setActiveTab("categories")}
          >
            Kategoriler
          </button>
          <button
            className={`tab-button ${activeTab === "items" ? "active" : ""}`}
            onClick={() => setActiveTab("items")}
          >
            Maliyet Kalemleri
          </button>
          <button
            className={`tab-button ${activeTab === "wastage" ? "active" : ""}`}
            onClick={() => setActiveTab("wastage")}
          >
            Fire Yönetimi
          </button>
        </div>
      </div>

      <div className="tab-content">
        {activeTab === "categories" && (
          <div className="categories-section">
            <div className="section-header">
              <h2>Maliyet Kategorileri</h2>
              <button
                className="create-button"
                onClick={() => navigate("/cost/categories/new")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
                </svg>
                Yeni Kategori
              </button>
            </div>

            <div className="categories-grid">
              {categories.map((category) => {
                const categoryItems = getCategoryItems(category.costCategoryId);
                const isExpanded = expandedCategories.has(
                  category.costCategoryId,
                );

                return (
                  <div
                    key={category.costCategoryId}
                    className="category-card-expanded"
                  >
                    <div className="category-header-row">
                      <div className="category-info">
                        <h3>{category.categoryName}</h3>
                        {category.description && (
                          <p className="category-description">
                            {category.description}
                          </p>
                        )}
                        <span className="category-item-count">
                          {categoryItems.length} maliyet kalemi
                        </span>
                      </div>
                      <div className="category-actions-row">
                        <button
                          className="action-btn edit-btn"
                          onClick={() => handleEditCategory(category)}
                          title="Düzenle"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                          Düzenle
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() =>
                            handleDeleteCategory(
                              category.costCategoryId,
                              category.categoryName,
                            )
                          }
                          title="Sil"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                          Sil
                        </button>
                        <button
                          className={`expand-btn ${
                            isExpanded ? "expanded" : ""
                          }`}
                          onClick={() =>
                            toggleCategory(category.costCategoryId)
                          }
                          title={isExpanded ? "Gizle" : "Göster"}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {isExpanded && categoryItems.length > 0 && (
                      <div className="category-items-list">
                        <div className="items-list-header">
                          <h4>Maliyet Kalemleri</h4>
                        </div>
                        <table className="category-items-table">
                          <thead>
                            <tr>
                              <th>Kalem Adı</th>
                              <th>Birim Fiyat</th>
                              <th>Döviz</th>
                              <th>Fire %</th>
                              <th>Tedarikçi</th>
                              <th>İşlemler</th>
                            </tr>
                          </thead>
                          <tbody>
                            {categoryItems.map((item) => (
                              <tr key={item.costItemId}>
                                <td>
                                  <div className="item-name">
                                    <strong>{item.itemName}</strong>
                                    {item.description && (
                                      <span className="item-description">
                                        {item.description}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="price-cell">
                                  {formatCurrency(
                                    item.unitPrice,
                                    item.currency,
                                  )}
                                  {item.discountPercentage && (
                                    <span className="discount-badge">
                                      -%{item.discountPercentage}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <span className="currency-badge">
                                    {item.currency}
                                  </span>
                                </td>
                                <td>
                                  {item.wastagePercentage ? (
                                    <span
                                      style={{
                                        color: "#ff9800",
                                        fontWeight: "500",
                                      }}
                                    >
                                      %{item.wastagePercentage}
                                    </span>
                                  ) : (
                                    <span style={{ color: "#ccc" }}>-</span>
                                  )}
                                </td>
                                <td>{item.supplier || "-"}</td>
                                <td>
                                  <div className="action-buttons">
                                    <button
                                      className="action-btn-small edit-btn"
                                      onClick={() => handleEditItem(item)}
                                      title="Düzenle"
                                    >
                                      <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                      >
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                      </svg>
                                    </button>
                                    <button
                                      className="action-btn-small delete-btn"
                                      onClick={() =>
                                        handleDeleteCostItem(
                                          item.costItemId,
                                          item.itemName,
                                        )
                                      }
                                      title="Sil"
                                    >
                                      <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                      >
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        <line x1="10" y1="11" x2="10" y2="17" />
                                        <line x1="14" y1="11" x2="14" y2="17" />
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {isExpanded && categoryItems.length === 0 && (
                      <div className="no-items-message">
                        Bu kategoriye ait maliyet kalemi bulunmuyor.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === "items" && (
          <div className="items-section">
            <div className="section-header">
              <h2>Maliyet Kalemleri</h2>
              <div className="section-header-actions">
                <div className="category-filter">
                  <label>Kategori:</label>
                  <select
                    value={selectedCategoryFilter}
                    onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                  >
                    <option value="">Tümü</option>
                    {categories.map((cat) => (
                      <option
                        key={cat.costCategoryId}
                        value={cat.costCategoryId}
                      >
                        {cat.categoryName}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  className="create-button"
                  onClick={() => navigate("/cost/items/new")}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
                  </svg>
                  Yeni Maliyet Kalemi
                </button>
              </div>
            </div>

            <div className="items-table-container">
              <table className="items-table">
                <thead>
                  <tr>
                    <th>Kalem Adı</th>
                    <th>Kategori</th>
                    <th>Birim Fiyat</th>
                    <th>Döviz</th>
                    <th>Fire %</th>
                    <th>Tedarikçi</th>
                    <th>Son Güncelleme</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {costItems
                    .filter((item) =>
                      selectedCategoryFilter
                        ? item.costCategoryId === selectedCategoryFilter
                        : true,
                    )
                    .map((item) => {
                      return (
                        <tr key={item.costItemId}>
                          <td>
                            <div className="item-name">
                              <strong>{item.itemName}</strong>
                              {item.description && (
                                <span className="item-description">
                                  {item.description}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="category-badge">
                              {item.costCategory?.categoryName ||
                                getCategoryName(item.costCategoryId)}
                            </span>
                          </td>
                          <td className="price-cell">
                            {formatCurrency(item.unitPrice, item.currency)}
                            {item.discountPercentage && (
                              <span className="discount-badge">
                                -%{item.discountPercentage}
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="currency-badge">
                              {item.currency}
                            </span>
                          </td>
                          <td>
                            {item.wastagePercentage ? (
                              <span
                                style={{ color: "#ff9800", fontWeight: "500" }}
                              >
                                %{item.wastagePercentage}
                              </span>
                            ) : (
                              <span style={{ color: "#ccc" }}>-</span>
                            )}
                          </td>
                          <td>{item.supplier || "-"}</td>
                          <td>{formatDate(item.lastPriceUpdate)}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="action-btn edit-btn"
                                onClick={() => handleEditItem(item)}
                                title="Düzenle"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                                Düzenle
                              </button>

                              <button
                                className="action-btn delete-btn"
                                onClick={() =>
                                  handleDeleteCostItem(
                                    item.costItemId,
                                    item.itemName,
                                  )
                                }
                                title="Sil"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                  <line x1="10" y1="11" x2="10" y2="17" />
                                  <line x1="14" y1="11" x2="14" y2="17" />
                                </svg>
                                Sil
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Fire Yönetimi Sekmesi */}
        {activeTab === "wastage" && (
          <div className="wastage-section">
            <div className="section-header">
              <h2>Fire Kategorileri</h2>
              <button
                className="create-button"
                onClick={() => {
                  setEditingWastageCategory({
                    wastageCategoryId: "",
                    categoryName: "",
                    description: "",
                    color: "#FF5722",
                    icon: "🔥",
                    displayOrder: wastageCategories.length + 1,
                    isActive: true,
                  });
                  setWastageModalOpen(true);
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
                </svg>
                Yeni Fire Kategorisi
              </button>
            </div>

            <div className="wastage-categories-grid">
              {wastageCategories.map((category) => (
                <div
                  key={category.wastageCategoryId}
                  className="wastage-category-card"
                >
                  <div
                    className="wastage-card-header"
                    style={{ borderLeftColor: category.color }}
                  >
                    <span className="category-icon">{category.icon}</span>
                    <h3>{category.categoryName}</h3>
                    <span className="item-count">
                      {category.itemCount || 0} fire kalemi
                    </span>
                  </div>
                  {category.description && (
                    <p className="category-description">
                      {category.description}
                    </p>
                  )}
                  <div className="card-actions">
                    <button
                      className="action-btn edit-btn"
                      onClick={() => {
                        setEditingWastageCategory(category);
                        setWastageModalOpen(true);
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Düzenle
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="section-header" style={{ marginTop: "40px" }}>
              <h2>Fire Kalemleri</h2>
              <button
                className="create-button"
                onClick={() => {
                  setEditingWastageItem({
                    wastageItemId: "",
                    wastageCategoryId: "",
                    itemName: "",
                    description: "",
                    defaultPercentage: 0,
                    unit: "Adet",
                    processStep: "",
                    reason: "",
                    isCalculated: false,
                    notes: "",
                    isActive: true,
                  });
                  setWastageItemModalOpen(true);
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
                </svg>
                Yeni Fire Kalemi
              </button>
            </div>

            <div className="filter-section">
              <select
                value={selectedWastageCategoryFilter}
                onChange={(e) =>
                  setSelectedWastageCategoryFilter(e.target.value)
                }
                className="category-filter"
              >
                <option value="">Tüm Kategoriler</option>
                {wastageCategories.map((cat) => (
                  <option
                    key={cat.wastageCategoryId}
                    value={cat.wastageCategoryId}
                  >
                    {cat.categoryName}
                  </option>
                ))}
              </select>
            </div>

            <div className="wastage-items-table">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fire Kalemi</th>
                    <th>Kategori</th>
                    <th>Varsayılan %</th>
                    <th>Birim</th>
                    <th>İşlem Adımı</th>
                    <th>Hesaplanan</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {wastageItems
                    .filter(
                      (item) =>
                        !selectedWastageCategoryFilter ||
                        item.wastageCategoryId ===
                          selectedWastageCategoryFilter,
                    )
                    .map((item) => (
                      <tr key={item.wastageItemId}>
                        <td>
                          <strong>{item.itemName}</strong>
                          {item.description && (
                            <div className="item-description">
                              {item.description}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className="category-badge">
                            {wastageCategories.find(
                              (c) =>
                                c.wastageCategoryId === item.wastageCategoryId,
                            )?.categoryName || "-"}
                          </span>
                        </td>
                        <td>
                          {item.defaultPercentage
                            ? `${item.defaultPercentage}%`
                            : "-"}
                        </td>
                        <td>{item.unit || "-"}</td>
                        <td>{item.processStep || "-"}</td>
                        <td>
                          <span
                            className={`status-badge ${
                              item.isCalculated ? "active" : "inactive"
                            }`}
                          >
                            {item.isCalculated ? "Evet" : "Hayır"}
                          </span>
                        </td>
                        <td>
                          <button
                            className="action-btn edit-btn"
                            onClick={() => {
                              setEditingWastageItem(item);
                              setWastageItemModalOpen(true);
                            }}
                          >
                            Düzenle
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModalOpen && editingItem && (
        <EditCostItemModal
          item={editingItem}
          categories={categories}
          onClose={() => {
            setEditModalOpen(false);
            setEditingItem(null);
          }}
          onSave={handleSaveEdit}
        />
      )}

      {/* Edit Category Modal */}
      {editCategoryModalOpen && editingCategory && (
        <EditCategoryModal
          category={editingCategory}
          onClose={() => {
            setEditCategoryModalOpen(false);
            setEditingCategory(null);
          }}
          onSave={handleSaveCategoryEdit}
        />
      )}

      {/* Fire Kategori Modal */}
      {wastageModalOpen && editingWastageCategory && (
        <WastageCategoryModal
          category={editingWastageCategory}
          onClose={() => {
            setWastageModalOpen(false);
            setEditingWastageCategory(null);
          }}
          onSave={handleSaveWastageCategory}
        />
      )}

      {/* Fire Kalemi Modal */}
      {wastageItemModalOpen && editingWastageItem && (
        <WastageItemModal
          item={editingWastageItem}
          categories={wastageCategories}
          onClose={() => {
            setWastageItemModalOpen(false);
            setEditingWastageItem(null);
          }}
          onSave={handleSaveWastageItem}
        />
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

interface EditCostItemModalProps {
  item: CostItem;
  categories: CostCategory[];
  onClose: () => void;
  onSave: (item: CostItem) => void;
}

const EditCostItemModal: React.FC<EditCostItemModalProps> = ({
  item,
  categories,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<CostItem>({ ...item });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Maliyet Kalemini Düzenle</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Kalem Adı *</label>
            <input
              type="text"
              value={formData.itemName}
              onChange={(e) =>
                setFormData({ ...formData, itemName: e.target.value })
              }
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Kategori *</label>
              <select
                value={formData.costCategoryId}
                onChange={(e) =>
                  setFormData({ ...formData, costCategoryId: e.target.value })
                }
                required
              >
                <option value="">Seçiniz</option>
                {categories.map((cat) => (
                  <option key={cat.costCategoryId} value={cat.costCategoryId}>
                    {cat.categoryName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Birim Fiyat *</label>
              <input
                type="number"
                step="0.01"
                value={formData.unitPrice}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    unitPrice: parseFloat(e.target.value),
                  })
                }
                required
              />
            </div>

            <div className="form-group">
              <label>Döviz *</label>
              <select
                value={formData.currency}
                onChange={(e) =>
                  setFormData({ ...formData, currency: e.target.value })
                }
                required
              >
                <option value="TRY">TRY</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Tedarikçi</label>
              <input
                type="text"
                value={formData.supplier || ""}
                onChange={(e) =>
                  setFormData({ ...formData, supplier: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label>Fire %</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData.wastagePercentage || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    wastagePercentage: parseFloat(e.target.value) || undefined,
                  })
                }
              />
            </div>
          </div>

          <div className="form-group">
            <label>Açıklama</label>
            <textarea
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>1. Miktar Etiketi</label>
              <input
                type="text"
                value={formData.quantity1Label || ""}
                onChange={(e) =>
                  setFormData({ ...formData, quantity1Label: e.target.value })
                }
                placeholder="Örn: En, Miktar, Adet"
              />
            </div>

            {formData.quantity2Label && (
              <div className="form-group">
                <label>2. Miktar Etiketi</label>
                <input
                  type="text"
                  value={formData.quantity2Label || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, quantity2Label: e.target.value })
                  }
                  placeholder="Örn: Boy, Çeşit Sayısı"
                />
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              İptal
            </button>
            <button type="submit" className="btn-primary">
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface EditCategoryModalProps {
  category: CostCategory;
  onClose: () => void;
  onSave: (category: CostCategory) => void;
}

const EditCategoryModal: React.FC<EditCategoryModalProps> = ({
  category,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<CostCategory>({ ...category });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Kategoriyi Düzenle</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Kategori Adı *</label>
            <input
              type="text"
              value={formData.categoryName}
              onChange={(e) =>
                setFormData({ ...formData, categoryName: e.target.value })
              }
              required
            />
          </div>

          <div className="form-group">
            <label>Açıklama</label>
            <textarea
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
            />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              İptal
            </button>
            <button type="submit" className="btn-primary">
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Fire Kategori Modal
interface WastageCategoryModalProps {
  category: WastageCategory | null;
  onClose: () => void;
  onSave: (category: WastageCategory) => void;
}

const WastageCategoryModal: React.FC<WastageCategoryModalProps> = ({
  category,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<WastageCategory>(
    category || {
      wastageCategoryId: "",
      categoryName: "",
      description: "",
      color: "#FF5722",
      icon: "🔥",
      displayOrder: 1,
      isActive: true,
    },
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>
            {category?.wastageCategoryId
              ? "Fire Kategorisini Düzenle"
              : "Yeni Fire Kategorisi"}
          </h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Kategori Adı *</label>
            <input
              type="text"
              value={formData.categoryName}
              onChange={(e) =>
                setFormData({ ...formData, categoryName: e.target.value })
              }
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>İkon</label>
              <input
                type="text"
                value={formData.icon || ""}
                onChange={(e) =>
                  setFormData({ ...formData, icon: e.target.value })
                }
                placeholder="🔥"
              />
            </div>

            <div className="form-group">
              <label>Renk</label>
              <input
                type="color"
                value={formData.color || "#FF5722"}
                onChange={(e) =>
                  setFormData({ ...formData, color: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label>Sıra</label>
              <input
                type="number"
                value={formData.displayOrder}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    displayOrder: parseInt(e.target.value),
                  })
                }
              />
            </div>
          </div>

          <div className="form-group">
            <label>Açıklama</label>
            <textarea
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
            />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              İptal
            </button>
            <button type="submit" className="btn-primary">
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Fire Kalemi Modal
interface WastageItemModalProps {
  item: WastageItem | null;
  categories: WastageCategory[];
  onClose: () => void;
  onSave: (item: WastageItem) => void;
}

const WastageItemModal: React.FC<WastageItemModalProps> = ({
  item,
  categories,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<WastageItem>(
    item || {
      wastageItemId: "",
      wastageCategoryId: "",
      itemName: "",
      description: "",
      defaultPercentage: 0,
      unit: "Adet",
      processStep: "",
      reason: "",
      isCalculated: false,
      notes: "",
      isActive: true,
    },
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>
            {item?.wastageItemId ? "Fire Kalemini Düzenle" : "Yeni Fire Kalemi"}
          </h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Kategori *</label>
            <select
              value={formData.wastageCategoryId}
              onChange={(e) =>
                setFormData({ ...formData, wastageCategoryId: e.target.value })
              }
              required
            >
              <option value="">Seçiniz</option>
              {categories.map((cat) => (
                <option
                  key={cat.wastageCategoryId}
                  value={cat.wastageCategoryId}
                >
                  {cat.categoryName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Kalem Adı *</label>
            <input
              type="text"
              value={formData.itemName}
              onChange={(e) =>
                setFormData({ ...formData, itemName: e.target.value })
              }
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Varsayılan % (Fire Yüzdesi)</label>
              <input
                type="number"
                step="0.01"
                value={formData.defaultPercentage || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    defaultPercentage: parseFloat(e.target.value) || undefined,
                  })
                }
              />
            </div>

            <div className="form-group">
              <label>Birim</label>
              <input
                type="text"
                value={formData.unit || ""}
                onChange={(e) =>
                  setFormData({ ...formData, unit: e.target.value })
                }
                placeholder="Adet, Kg, Metre..."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>İşlem Adımı</label>
              <input
                type="text"
                value={formData.processStep || ""}
                onChange={(e) =>
                  setFormData({ ...formData, processStep: e.target.value })
                }
                placeholder="Kesim, Dikim, Boya..."
              />
            </div>

            <div className="form-group">
              <label>Hesaplanan Fire mi?</label>
              <select
                value={formData.isCalculated ? "true" : "false"}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    isCalculated: e.target.value === "true",
                  })
                }
              >
                <option value="false">Hayır (Manuel)</option>
                <option value="true">Evet (Otomatik)</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Sebep</label>
            <input
              type="text"
              value={formData.reason || ""}
              onChange={(e) =>
                setFormData({ ...formData, reason: e.target.value })
              }
              placeholder="Hatalı kesim, kumaş hatası..."
            />
          </div>

          <div className="form-group">
            <label>Açıklama</label>
            <textarea
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={2}
            />
          </div>

          <div className="form-group">
            <label>Notlar</label>
            <textarea
              value={formData.notes || ""}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              rows={2}
            />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              İptal
            </button>
            <button type="submit" className="btn-primary">
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Ana component'ın JSX'inin sonuna alert ve confirm modal ekle
// Bu, CostManagement component'ının içinde olmalı

export default CostManagement;
